import { useEffect, useId, useSyncExternalStore } from "react";

const waiting = new Map<string, number>();
const listeners = new Set<() => void>();
let blocked = false;
let active: string | null = null;
let closing: string | null = null;
function publish() {
  const next =
    blocked || closing
      ? null
      : active && waiting.has(active)
        ? active
        : ([...waiting].sort((a, b) => a[1] - b[1])[0]?.[0] ?? null);
  if (active !== null && (blocked || !waiting.has(active))) {
    // AnimatePresence keeps a dismissed dialog mounted during its exit.
    // Wait for that exit before mounting the next dialog.
    closing = active;
    active = null;
    listeners.forEach((listener) => listener());
    return;
  }
  if (active !== next) {
    active = next;
    listeners.forEach((listener) => listener());
  }
}
export function setCelebrationsBlocked(value: boolean) {
  blocked = value;
  publish();
}
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
const snapshot = () => active;

/** Queue meaningful celebrations; first-run setup and active study take priority. */
export function useCelebrationSlot(requested: boolean, priority: number) {
  const id = useId();
  const current = useSyncExternalStore(subscribe, snapshot, () => null);
  useEffect(() => {
    if (requested) waiting.set(id, priority);
    else waiting.delete(id);
    publish();
  }, [requested, priority, id]);
  useEffect(
    () => () => {
      waiting.delete(id);
      if (active === id) active = null;
      if (closing === id) closing = null;
      publish();
    },
    [id],
  );
  return {
    visible: requested && current === id,
    onExitComplete: () => {
      if (closing === id) {
        closing = null;
        publish();
      }
    },
  };
}
